<script setup>
import { ref, computed, onMounted } from "vue";
import { fetchPointsBreakdown, updatePointsBreakdown } from "./api";

const breakdown = ref([]);
const loading = ref(false);
const saving = ref(false);
const error = ref("");
const success = ref(false);
const search = ref("");
const showAddDialog = ref(false);
const newActivity = ref({ activity: "", points_deducted: 0, points_awarded: 0, interval: "" });

const filteredBreakdown = computed(() => {
  if (!search.value) return breakdown.value;
  const q = search.value.toLowerCase();
  return breakdown.value.filter((item) =>
    (item.activity ?? item.name ?? item.key ?? "").toLowerCase().includes(q)
  );
});

onMounted(async () => {
  loading.value = true;
  try { breakdown.value = await fetchPointsBreakdown(); }
  catch { error.value = "Couldn't load points breakdown."; }
  finally { loading.value = false; }
});

function openAddDialog() {
  newActivity.value = { activity: "", points_deducted: 0, points_awarded: 0, interval: "" };
  showAddDialog.value = true;
}

function addActivity() {
  breakdown.value.push({ ...newActivity.value });
  showAddDialog.value = false;
}

async function save() {
  saving.value = true; error.value = ""; success.value = false;
  try { await updatePointsBreakdown(breakdown.value); success.value = true; }
  catch { error.value = "Couldn't save. Please try again."; }
  finally { saving.value = false; }
}
</script>

<template>
  <section>
    <div class="d-flex align-center justify-space-between mb-4 gap-3 flex-wrap">
      <h1 class="text-h5">Points Distribution</h1>
      <VBtn @click="openAddDialog">Add Activity</VBtn>
    </div>
    <VTextField
      v-model="search"
      placeholder="Search activities…"
      prepend-inner-icon="ri-search-line"
      clearable
      class="mb-4"
      density="compact"
      style="max-width:400px"
    />
    <p v-if="loading">Loading…</p>
    <VCard v-else class="pa-4">
      <VAlert v-if="error" type="error" class="mb-4">{{ error }}</VAlert>
      <VAlert v-if="success" type="success" class="mb-4">Saved successfully.</VAlert>
      <VTable density="compact" class="mb-4">
        <thead>
          <tr>
            <th>Activity</th>
            <th>Points Deducted</th>
            <th>Points Awarded</th>
            <th>Interval</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, i) in filteredBreakdown" :key="i">
            <td>{{ item.activity ?? item.name ?? item.key }}</td>
            <td><VTextField v-model.number="item.points_deducted" type="number" density="compact" hide-details style="width:120px" /></td>
            <td><VTextField v-model.number="item.points_awarded" type="number" density="compact" hide-details style="width:120px" /></td>
            <td><VTextField v-model="item.interval" density="compact" hide-details style="width:120px" /></td>
          </tr>
        </tbody>
      </VTable>
      <VBtn :loading="saving" @click="save">Save Changes</VBtn>
    </VCard>

    <VDialog v-model="showAddDialog" max-width="450">
      <VCard>
        <VCardTitle class="pt-4 px-4">Add Activity</VCardTitle>
        <VCardText class="d-flex flex-column gap-3">
          <VTextField v-model="newActivity.activity" label="Activity Name" />
          <VTextField v-model.number="newActivity.points_deducted" label="Points Deducted" type="number" />
          <VTextField v-model.number="newActivity.points_awarded" label="Points Awarded" type="number" />
          <VTextField v-model="newActivity.interval" label="Interval" />
        </VCardText>
        <VCardActions class="px-4 pb-4 justify-end gap-2">
          <VBtn variant="tonal" @click="showAddDialog = false">Cancel</VBtn>
          <VBtn :disabled="!newActivity.activity" @click="addActivity">Add</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </section>
</template>
